import { NavLink } from "react-router-dom";
import { authClient, signOut } from "@/lib/auth-client";
import { useCuits } from "../hooks/useCuits";
import { formatCuitDisplay } from "@repo/shared";
import {
  LayoutDashboard,
  Upload,
  Receipt,
  Undo2,
  Server,
  Settings,
  Users,
  Plus,
  Menu,
  BellRing,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetOverlay, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { CuitEnvironmentBadge } from "@/components/CuitIdentity";
import { GlobalLoadingBar } from "@/components/GlobalLoadingBar";
import { InvoiceJobCenter } from "@/components/InvoiceJobCenter";
import { useInvoiceJobCenter } from "@/hooks/useInvoiceJobs";

export default function Layout({ children }: { children: React.ReactNode }) {
  const { cuits, activeCuit, setActiveCuitId } = useCuits();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { activeCount, setOpen } = useInvoiceJobCenter();

  const navLinks = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/transfers", icon: Upload, label: "Transferencias" },
    { to: "/invoices", icon: Receipt, label: "Facturas" },
    { to: "/credit-notes", icon: Undo2, label: "Notas de Crédito" },
    { to: "/arca-status", icon: Server, label: "ARCA" },
  ];

  const adminLinks = [
    { to: "/cuit/settings", icon: Settings, label: "Configuración" },
    { to: "/cuit/users", icon: Users, label: "Usuarios" },
  ];

  const sidebarContent = (
    <>
      <div className="px-6 py-8 border-b-2 border-white/10 flex items-center gap-3">
        <div className="size-8 bg-[var(--accent-raw)] flex items-center justify-center font-mono font-bold text-lg text-white">
          F
        </div>
        <span className="font-bold text-xl text-white tracking-tighter uppercase">Facturador</span>
      </div>

      {/* CUIT selector */}
      {cuits.length > 0 && (
        <div className="border-b-2 border-white/10">
          <div className="px-6 py-3 text-[0.65rem] font-bold uppercase tracking-[0.2em] text-white/40 font-mono">
            Entidad Activa
          </div>
          <div className="px-4 pb-4">
            {cuits.map((cuit) => (
              <Button
                key={cuit.id}
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  "flex h-auto w-full flex-col items-start justify-center px-3 py-2 bg-transparent border-l-2 border-transparent text-[var(--ink-sidebar)] transition-all text-left hover:bg-white/5 hover:text-white shadow-none active:translate-x-0 active:translate-y-0",
                  activeCuit?.id === cuit.id &&
                  "bg-white/10 text-white border-[var(--accent-raw)] hover:border-[var(--accent-raw)]",
                )}
                onClick={() => setActiveCuitId(cuit.id)}
              >
                <span className="font-semibold text-sm truncate uppercase tracking-tight">{cuit.razonSocial}</span>
                <div className="mt-1 flex w-full items-center justify-between gap-2">
                  <span className="font-mono text-xs opacity-70">{formatCuitDisplay(cuit.cuit)}</span>
                  <CuitEnvironmentBadge
                    environment={cuit.environment}
                    className="px-1.5 py-0.5 text-[0.55rem] shadow-none"
                  />
                </div>
              </Button>
            ))}
          </div>
        </div>
      )}

      <nav className="flex-1 px-4 py-6 flex flex-col gap-1">
        <div className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-white/40 font-mono px-3 mb-2">
          Operaciones
        </div>
        {navLinks.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 text-sm font-mono tracking-tight no-underline transition-all cursor-pointer border-none bg-transparent w-full text-left text-[var(--ink-sidebar)] hover:bg-white/10 hover:text-white [&_svg]:size-[16px] [&_svg]:opacity-70 [&_svg]:shrink-0",
                isActive &&
                "bg-white text-black font-semibold hover:bg-white hover:text-black [&_svg]:opacity-100",
              )
            }
            onClick={() => setSidebarOpen(false)}
          >
            <link.icon strokeWidth={2} />
            {link.label}
          </NavLink>
        ))}

        {activeCuit?.isOwner && (
          <>
            <div className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-white/40 font-mono px-3 pt-6 mb-2">
              Administración
            </div>
            {adminLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 text-sm font-mono tracking-tight no-underline transition-all cursor-pointer border-none bg-transparent w-full text-left text-[var(--ink-sidebar)] hover:bg-white/10 hover:text-white [&_svg]:size-[16px] [&_svg]:opacity-70 [&_svg]:shrink-0",
                    isActive &&
                    "bg-white text-black font-semibold hover:bg-white hover:text-black [&_svg]:opacity-100",
                  )
                }
                onClick={() => setSidebarOpen(false)}
              >
                <link.icon strokeWidth={2} />
                {link.label}
              </NavLink>
            ))}
          </>
        )}

        <div className="flex-1" />

        <NavLink
          to="/cuit/new"
          className="flex items-center gap-3 px-3 py-3 mt-4 text-[var(--accent-raw)] font-mono text-sm uppercase tracking-tight font-semibold hover:bg-[var(--accent-raw)] hover:text-white transition-colors [&_svg]:size-[16px]"
          onClick={() => setSidebarOpen(false)}
        >
          <Plus strokeWidth={2.5} />
          Agregar CUIT
        </NavLink>
      </nav>

      <div className="px-6 py-5 border-t-2 border-white/10 bg-black">
        <div className="flex items-center gap-3">
          <UserAvatar />
          <button
            type="button"
            onClick={() => void signOut()}
            className="text-white font-mono text-xs uppercase tracking-widest hover:text-[var(--accent-raw)]"
          >
            Salir
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-[hsl(var(--background))] selection:bg-[var(--accent-raw)] selection:text-white">
      {/* Mobile Sheet */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetOverlay open={sidebarOpen} onClose={() => setSidebarOpen(false)} className="lg:hidden" />
        <SheetContent open={sidebarOpen} className="lg:hidden bg-[var(--bg-sidebar)] p-0 border-l-0 flex flex-col rounded-none w-[280px]">
          {sidebarContent}
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <aside className="w-[280px] min-h-screen bg-[var(--bg-sidebar)] hidden lg:flex flex-col fixed top-0 left-0 bottom-0 z-40 border-r-2 border-[#1a1a1a]">
        {sidebarContent}
      </aside>

      <main className="flex-1 lg:ml-[280px] min-h-screen flex flex-col min-w-0 overflow-x-clip">
        {/* Mobile sticky header */}
        <div className="sticky top-0 z-50 lg:hidden flex items-center gap-3 px-3 h-14 bg-white border-b-2 border-black">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] bg-white hover:bg-black hover:text-white"
          >
            <Menu size={20} strokeWidth={2} />
          </Button>
          <div className="flex items-center gap-2 font-black text-sm tracking-tighter uppercase">
            <div className="size-6 bg-[var(--accent-raw)] flex items-center justify-center font-mono font-black text-xs text-white">
              F
            </div>
            FACTURADOR
          </div>
          {activeCuit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpen(true)}
              className="ml-auto border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] bg-white hover:bg-black hover:text-white relative"
              title="Centro de facturación"
            >
              <BellRing size={18} strokeWidth={2.2} />
              {activeCount > 0 && (
                <span className="absolute -right-1 -top-1 min-w-4 h-4 px-1 rounded-none border border-black bg-[var(--error)] text-[10px] leading-[14px] font-black text-white">
                  {activeCount}
                </span>
              )}
            </Button>
          )}
        </div>
        <GlobalLoadingBar />
        <div key={activeCuit?.id ?? "none"} className="flex-1 flex flex-col">
          {children}
        </div>
        <InvoiceJobCenter />
      </main>
    </div>
  );
}

function UserAvatar() {
  const { data: session } = authClient.useSession();
  const user = session?.user;
  if (!user) return null;

  if (user.image) {
    return (
      <img
        src={user.image}
        alt={user.name}
        title={user.email}
        referrerPolicy="no-referrer"
        className="size-9 border-2 border-white object-cover"
      />
    );
  }

  return (
    <div
      title={user.email}
      className="size-9 border-2 border-white flex items-center justify-center font-mono font-bold text-white"
    >
      {(user.name || user.email).charAt(0).toUpperCase()}
    </div>
  );
}
